// src/pages/documents/SuratJalanListPage/SuratJalanListPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import ShipmentsAPI from '../../../api/v3/shipments-api-v3';

const STATUSES = ['send_to_vendor', 'pick_up_by_customer', 'delivered'];

const includesCi = (h = '', n = '') =>
  (h || '').toString().toLowerCase().includes((n || '').toString().toLowerCase());

// ---- vendor helpers (dynamic) ----
const normVendor = (v) => String(v || '').toLowerCase();
const vendorLabel = (v) => {
  switch (normVendor(v)) {
    case 'kaya': return 'Kaya Leathers';
    case 'bnk':  return 'BNK';
    default:     return v ? String(v) : '-';
  }
};

// ---- filename helpers ----
function safePart(s) {
  return String(s || '').replace(/[^\w.-]+/g, '_').slice(0, 80);
}
function buildFilename(row) {
  const id     = row?.id ?? 'unknown';
  const ordNo  = row?.order_no || `ORD-${row?.order_id || id}`;
  const vendor = vendorLabel(row?.vendor);
  return `Surat_Jalan_${safePart(ordNo)}_${safePart(vendor)}.pdf`;
}

// Quick probe for real PDF bytes (handles servers that send octet-stream)
async function isPdfResponse(res) {
  const ct = (res.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('application/pdf')) return true;

  // Fallback: sniff first 5 bytes for "%PDF-"
  try {
    const buf = await res.clone().arrayBuffer();
    const head = new Uint8Array(buf.slice(0, 5));
    const sig = Array.from(head).map((n) => String.fromCharCode(n)).join('');
    return sig === '%PDF-';
  } catch {
    return false;
  }
}

export default function SuratJalanListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [downloading, setDownloading] = useState(null);

  // filters + paging
  const [q, setQ] = useState('');
  const [per, setPer] = useState(25);
  const [page, setPage] = useState(1);

  // NEW: safe PDF toggle (uses GS-normalized PDF from backend)
  const [safePdf, setSafePdf] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErr('');

      // Fetch all three statuses in parallel, then merge & dedupe by id.
      const responses = await Promise.all(STATUSES.map(s => ShipmentsAPI.list({ status: s })));

      const merged = responses.flatMap(res => {
        const payload = res?.items ?? res?.data ?? (Array.isArray(res) ? res : []);
        return Array.isArray(payload) ? payload : [];
      });

      const byId = new Map();
      for (const it of merged) {
        if (it && it.id != null) byId.set(it.id, it); // last one wins (most recent in merged order)
      }
      setRows(Array.from(byId.values()));
    } catch (e) {
      // Fallback: unfiltered fetch then filter client-side
      try {
        const res = await ShipmentsAPI.list({});
        const payload = res?.items ?? res?.data ?? (Array.isArray(res) ? res : []);
        const onlyWanted = (Array.isArray(payload) ? payload : []).filter(
          r => STATUSES.includes((r?.status || '').toString())
        );
        setRows(onlyWanted);
      } catch (e2) {
        setErr(e2.message || e.message || 'Failed to fetch');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // search now checks dynamic vendor label
  const filtered = useMemo(() => {
    if (!q) return rows;
    return rows.filter(r =>
      includesCi(r.order_no, q) ||
      includesCi(r.customer?.name, q) ||
      includesCi(r.customer_address?.city, q) ||
      includesCi(vendorLabel(r.vendor), q)
    );
  }, [rows, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / per));
  const pageSafe = Math.min(page, totalPages);
  const start = (pageSafe - 1) * per;
  const paged = useMemo(() => filtered.slice(start, start + per), [filtered, start, per]);

  async function handleDownload(row) {
    const id = row?.id;
    if (!id) return;

    setDownloading(id);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE;
      const qs = new URLSearchParams();
      if (safePdf) qs.set('safe', '1');
      const url = `${API_BASE}/api/v3/shipments/${id}/surat_jalan${qs.toString() ? `?${qs}` : ''}`;

      const res = await fetch(url, {
        headers: { Accept: 'application/pdf' },
        cache: 'no-store'
      });

      if (!res.ok) {
        let msg = `Failed to download Surat Jalan for ID ${id}`;
        try { msg += `\n\n${(await res.text()).slice(0, 400)}`; } catch {}
        throw new Error(msg);
      }

      const looksPdf = await isPdfResponse(res);
      if (!looksPdf) {
        const textProbe = await res.clone().text().catch(() => '');
        throw new Error(
          `Unexpected content-type (not PDF).${textProbe ? `\nSnippet: ${textProbe.slice(0, 300)}…` : ''}`
        );
      }

      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = buildFilename(row);
      a.click();
      window.URL.revokeObjectURL(a.href);
    } catch (e) {
      alert(e.message);
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div className="bagspa-page surat-jalan-page">
      <div className="bagspa-page-header surat-jalan-page-header">
        <h3 className="mb-0">Surat Jalan (Send to Vendor)</h3>
      </div>

      <div className="bagspa-filter-card surat-jalan-control-card">
        <div className="surat-jalan-control-row">
          <div className="bagspa-filter-field surat-jalan-filter-search">
            <label className="form-label">Search</label>
            <input
              className="form-control"
              placeholder="Search order no / customer / city / vendor..."
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
            />
          </div>

          <div className="bagspa-filter-field surat-jalan-filter-rows">
            <label className="form-label">Rows</label>
            <select
              className="form-control"
              value={per}
              onChange={(e) => {
                setPer(parseInt(e.target.value, 10) || 25);
                setPage(1);
              }}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}/page
                </option>
              ))}
            </select>
          </div>

          <div className="bagspa-filter-field surat-jalan-filter-safe-pdf">
            <label className="form-label">PDF</label>
            <div
              className="surat-jalan-check-row"
              title="Use Ghostscript-normalized PDF to avoid garbled prints"
            >
              <div className="form-check">
                <input
                  id="sjSafePdf"
                  className="form-check-input"
                  type="checkbox"
                  checked={safePdf}
                  onChange={(e) => setSafePdf(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="sjSafePdf">
                  Safe PDF
                </label>
              </div>
            </div>
          </div>

          <div className="surat-jalan-filter-refresh">
            <button className="btn btn-primary" onClick={load}>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className="bagspa-table-card surat-jalan-table-card">
          <div className="table-responsive">
            <table className="table table-bordered table-sm align-middle surat-jalan-table">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 56 }}>#</th>
                  <th>Order</th>
                  <th>Type</th>
                  <th>Vendor</th>
                  <th>City</th>
                  <th>Updated</th>
                  <th style={{ width: 160 }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {paged.length > 0 ? (
                  paged.map((r, i) => {
                    const isBnk = normVendor(r.vendor) === 'bnk';

                    return (
                      <tr key={r.id}>
                        <td>{start + i + 1}</td>

                        <td className="text-monospace">
                          {r.order_no || `ORD-${r.order_id}`}
                        </td>

                        <td>{r.shipment_type || '-'}</td>
                        <td>{vendorLabel(r.vendor)}</td>
                        <td>{r.customer_address?.city || '-'}</td>

                        <td>
                          {r.updated_at
                            ? new Date(r.updated_at).toLocaleDateString('id-ID')
                            : '-'}
                        </td>

                        <td>
                          <div className="surat-jalan-row-actions">
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => !isBnk && handleDownload(r)}
                              disabled={downloading === r.id || isBnk}
                              title={isBnk ? 'Surat Jalan tidak tersedia untuk vendor BNK' : ''}
                            >
                              {downloading === r.id ? 'Downloading…' : 'Download PDF'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-muted">
                      No vendor shipments found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="bagspa-pagination-row surat-jalan-pagination-row">
            <div className="text-muted small">
              Total: {filtered.length} • Page {pageSafe} / {totalPages}
            </div>

            <div className="btn-group">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe <= 1}
                onClick={() => setPage(1)}
              >
                &laquo;
              </button>

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                &lsaquo;
              </button>

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                &rsaquo;
              </button>

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                &raquo;
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
